import React, { useState } from 'react';
import { Button, Badge } from '@/components/ui';
import { GitCompare, ChevronDown } from 'lucide-react';

interface ScenarioOption {
  value: string;
  label: string;
  isBaseline: boolean;
}

interface ScenarioSelectorProps {
  scenarios: ScenarioOption[];
  onCompare: (scenarioIds: string[]) => void;
}

const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({
  scenarios,
  onCompare,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);

  const handleScenarioToggle = (scenarioId: string) => {
    setSelectedScenarios(prev => {
      if (prev.includes(scenarioId)) {
        return prev.filter(id => id !== scenarioId);
      } else if (prev.length < 3) { // Limit to 3 scenarios for comparison
        return [...prev, scenarioId];
      }
      return prev;
    });
  };

  const handleCompare = () => {
    if (selectedScenarios.length >= 2) {
      onCompare(selectedScenarios);
      setSelectedScenarios([]);
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedScenarios([]);
  };

  if (scenarios.length < 2) {
    return null; // Don't show if there aren't enough scenarios to compare
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        leftIcon={<GitCompare className="w-4 h-4" />}
        rightIcon={<ChevronDown className="w-4 h-4" />}
      >
        Compare Scenarios
        {selectedScenarios.length > 0 && (
          <Badge variant="primary" className="ml-2">
            {selectedScenarios.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-muted border border-border rounded-lg shadow-lg z-10">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">Select Scenarios to Compare</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>

            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {scenarios.map((scenario) => (
                <label
                  key={scenario.value}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedScenarios.includes(scenario.value)}
                    onChange={() => handleScenarioToggle(scenario.value)}
                    disabled={!selectedScenarios.includes(scenario.value) && selectedScenarios.length >= 3}
                    className="w-4 h-4 text-[var(--accent)] bg-muted/50 border-border rounded focus:ring-[var(--accent)]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-foreground text-sm">{scenario.label}</span>
                      {scenario.isBaseline && (
                        <Badge variant="primary" className="text-xs">
                          Baseline
                        </Badge>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {selectedScenarios.length >= 3 && (
              <div className="text-xs text-status-warning mb-4">
                Maximum 3 scenarios can be compared at once
              </div>
            )}

            <div className="flex justify-between space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={selectedScenarios.length === 0}
              >
                Clear All
              </Button>
              <Button
                size="sm"
                onClick={handleCompare}
                disabled={selectedScenarios.length < 2}
              >
                Compare ({selectedScenarios.length})
              </Button>
            </div>

            {selectedScenarios.length === 1 && (
              <div className="text-xs text-muted-foreground mt-2">
                Select at least 2 scenarios to compare
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioSelector;